import { stripPrefix } from "shared/utils/vrn";

export const getPrimaryApplicantDealFromRawApplications = (
  rawApplications: any[] = []
) => {
  const primaryApplicationData = rawApplications.find(
    (application) => application?.isPrimaryApplication
  );

  if (!primaryApplicationData) {
    return undefined;
  }

  const rawTerm = Number(
    primaryApplicationData?.applicationSummary?.preferredLeaseDurationMonths
  );
  const preferredLeaseDurationMonths =
    Number.isFinite(rawTerm) && rawTerm > 0 ? rawTerm : undefined;
  const preferredLeaseStartDate =
    primaryApplicationData?.applicationSummary?.preferredLeaseStartDate;
  const summaryIsSubmitted =
    primaryApplicationData?.applicationSummary?.isSubmitted;
  const hasSubmissionSignal =
    typeof summaryIsSubmitted === "boolean" ||
    Boolean(primaryApplicationData?.submittedAt);
  const isSubmitted =
    typeof summaryIsSubmitted === "boolean"
      ? summaryIsSubmitted
      : Boolean(primaryApplicationData?.submittedAt);

  if (
    !preferredLeaseDurationMonths &&
    !preferredLeaseStartDate &&
    !hasSubmissionSignal
  ) {
    return undefined;
  }

  return {
    preferredLeaseDurationMonths,
    preferredLeaseStartDate,
    isSubmitted,
  };
};

const normalizeVrn = (value?: string | number) => {
  if (value === undefined || value === null) {
    return undefined;
  }

  return stripPrefix(String(value));
};

const normalizeApplicantReference = (value?: string | number) => {
  const normalizedVrn = normalizeVrn(value);
  if (!normalizedVrn) {
    return undefined;
  }

  return normalizedVrn.replace(/^applicant\//i, "");
};

const normalizeEmail = (value?: string) => {
  if (!value) {
    return undefined;
  }

  const normalized = String(value).trim().toLowerCase();
  return normalized || undefined;
};

const normalizeParty = (value?: string) => {
  if (!value) {
    return undefined;
  }

  const normalized = String(value).trim().toLowerCase();
  return normalized || undefined;
};

const normalizeName = (value?: string) => {
  if (!value) {
    return undefined;
  }

  const normalized = String(value).trim().toLowerCase().replace(/\s+/g, " ");
  return normalized || undefined;
};

const partyMatches = (left?: string, right?: string) =>
  !left || !right || left === right;

export const getLegacyAppForApplicant = (
  apps: any[] = [],
  applicant?: {
    id?: string;
    legacyId?: string | number;
    email?: string;
    party?: string;
    firstName?: string;
    lastName?: string;
  },
  /** Mapped deal applications from `useGetApplicationSummary` (includes `sentilink`); optional. */
  dealApplications?: ReadonlyArray<{ id?: string | number; sentilink?: unknown }>
) => {
  if (!Array.isArray(apps) || !applicant) {
    return undefined;
  }

  const applicantVrn = normalizeVrn(applicant.id);
  const legacyId =
    applicant.legacyId === undefined || applicant.legacyId === null
      ? undefined
      : String(applicant.legacyId);
  const applicantEmail = normalizeEmail(applicant.email);
  const applicantParty = normalizeParty(applicant.party);
  const applicantName = normalizeName(
    [applicant.firstName, applicant.lastName].filter(Boolean).join(" ")
  );

  const legacy = apps.find((app) => {
    const appVrn = normalizeVrn(app?.applicantVrn);
    const appId =
      app?.appId === undefined || app?.appId === null
        ? undefined
        : String(app.appId);
    const appEmail = normalizeEmail(app?.info?.email);
    const appParty = normalizeParty(app?.party);
    const appName = normalizeName(app?.info?.name);

    const matchesByVrn = Boolean(applicantVrn) && appVrn === applicantVrn;
    const matchesByLegacyId = Boolean(legacyId) && appId === legacyId;
    const matchesByEmail =
      Boolean(applicantEmail) &&
      appEmail === applicantEmail &&
      partyMatches(applicantParty, appParty);
    const matchesByName =
      Boolean(applicantName) &&
      appName === applicantName &&
      partyMatches(applicantParty, appParty);

    return matchesByVrn || matchesByLegacyId || matchesByEmail || matchesByName;
  });

  if (!legacy || !dealApplications?.length) {
    return legacy;
  }

  const rowAppIdSource = legacy.appId ?? legacy.info?.applicationId;
  const rowAppId =
    rowAppIdSource === undefined || rowAppIdSource === null
      ? undefined
      : String(rowAppIdSource);
  const dealApp =
    rowAppId === undefined
      ? undefined
      : dealApplications.find(
          (a) =>
            a.id !== undefined &&
            a.id !== null &&
            String(a.id) === rowAppId
        );
  if (!dealApp) {
    return legacy;
  }

  return { ...legacy, sentilink: dealApp.sentilink };
};

export const getRawApplicationForApplicant = (
  rawApplications: any[] = [],
  applicant?: {
    id?: string;
    legacyId?: string | number;
  },
  legacyApp?: {
    appId?: string | number;
  }
) => {
  if (!Array.isArray(rawApplications) || !applicant) {
    return undefined;
  }

  const applicantRef = normalizeApplicantReference(applicant.id);
  const legacyIdSource =
    legacyApp?.appId === undefined || legacyApp?.appId === null
      ? applicant.legacyId
      : legacyApp.appId;
  const legacyId =
    legacyIdSource === undefined || legacyIdSource === null
      ? undefined
      : String(legacyIdSource);

  return rawApplications.find((app) => {
    const appRef = normalizeApplicantReference(app?.applicantVrn);
    const appId =
      app?.id === undefined || app?.id === null ? undefined : String(app.id);

    const matchesByVrn = Boolean(applicantRef) && appRef === applicantRef;
    const matchesByLegacyId = Boolean(legacyId) && appId === legacyId;

    return matchesByVrn || matchesByLegacyId;
  });
};
